import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import { IconBadgeVo } from '@tabler/icons-react';
import { Loader2 } from 'lucide-react';
import { useCxExpToast } from '../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from './ConfigStatusView2Skeleton';
import './ConfigStatusView2.css';

function Pcs2Switch({ checked, onChange, ariaLabel }) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };

  return (
    <label className="pcs2-switch">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Subblock({ hideHead, children }) {
  return <div className="pcs2-subblock">{hideHead ? children : children}</div>;
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} stroke={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

/**
 * Processos → Configuração de colunas (layout pcs2).
 */
export default function ConfigColunasView({ apiBaseUrl }) {
  const [visualizarColunaStatus, setVisualizarColunaStatus] = useState(true);
  const [savedValue, setSavedValue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiBaseUrl}/api/config/visualizacao-coluna-status`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success && result.data != null) {
        const v = result.data.asstec_osopen_shstatus;
        const enabled = v === 1 || v === '1';
        setVisualizarColunaStatus(enabled);
        setSavedValue(enabled);
      } else {
        setSavedValue(true);
      }
    } catch (e) {
      console.warn('Erro ao carregar visualização coluna status:', e);
      toastError('Erro ao carregar configuração');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, toastError]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isDirty = savedValue !== null && visualizarColunaStatus !== savedValue;

  const saveConfig = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiBaseUrl}/api/config/visualizacao-coluna-status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ asstec_osopen_shstatus: visualizarColunaStatus ? 1 : 0 }),
      });
      const result = await response.json();
      if (result.success) {
        setSavedValue(visualizarColunaStatus);
        toastSuccess('Configuração salva com sucesso!');
      } else {
        toastError(result.message || 'Erro ao salvar configuração');
      }
    } catch (e) {
      console.warn('Erro ao salvar visualização coluna status:', e);
      toastError('Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <ProcessConfigSkeleton variant="settings" ariaLabel="Carregando configuração de colunas" />;
  }

  return (
    <div className="pcs2-page">
      <Pcs2Section
        icon={IconBadgeVo}
        title="Configuração de colunas"
        desc="Configure a exibição de colunas em listagens"
      >
        <Pcs2Subblock hideHead>
          <div className="pcs2-card pcs2-card--flat">
            <Pcs2SettingRow
              title="Visualizar status na coluna Em aberto"
              desc="Ao habilitar, o código do status do procesos será exibido a direita do número da ordem de serviço"
            >
              <Pcs2Switch
                checked={visualizarColunaStatus}
                onChange={(e) => setVisualizarColunaStatus(e.target.checked)}
                ariaLabel="Visualizar status na coluna Em aberto"
              />
            </Pcs2SettingRow>
          </div>
        </Pcs2Subblock>
      </Pcs2Section>

      {Toaster}

      {createPortal(
        <button
          type="button"
          className="pcs2-save-fab"
          onClick={saveConfig}
          disabled={isSaving || !isDirty}
          aria-label="Salvar configuração"
        >
          {isSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
          {isSaving ? 'Salvando...' : 'Salvar configuração'}
        </button>,
        document.body
      )}
    </div>
  );
}
